import React, { useImperativeHandle, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApolloQueryResult } from '@apollo/client';
import { setSuffix } from '@/utils/helpers';
import { useToggleOpenState } from '@/hooks/useToggleOpenState';
import { AdPageTestIds } from '@/__e2e__/testIds/ad-page';
import { WarningModal } from '@/components/Ad/WarningModal';
import { CommonTestIds } from '@/__e2e__/testIds/common';
import { Button } from '@/components/UI/Button/Button';

type Props<T> = {
  onSuccess?: () => void | Promise<ApolloQueryResult<T>>;
  dataTestId?: string;
};

export type RestoredAdPopupRef = {
  toggle: () => void;
};

const RestoredAdPopup = <T,>(
  { onSuccess, dataTestId }: Props<T>,
  ref: React.Ref<RestoredAdPopupRef>
) => {
  const { t, i18n } = useTranslation();
  const [isSuccessPopupOpen, toggleSuccessPopup] = useToggleOpenState();
  const [isLoading, setIsLoading] = useState(false);

  const description = t(setSuffix(i18n, '30', 'pages.ad.ad_published_for'), {
    days: '30',
  });

  useImperativeHandle(
    ref,
    () => ({
      toggle: () => toggleSuccessPopup(),
    }),
    []
  );

  async function onOkClick() {
    if (onSuccess) {
      setIsLoading(true);
      await onSuccess();
      setIsLoading(false);
    }
    toggleSuccessPopup();
  }

  return (
    <WarningModal
      dataTestId={dataTestId ?? AdPageTestIds.popup.restoredSuccess}
      isOpen={isSuccessPopupOpen}
      onClose={() => toggleSuccessPopup(false)}
      icon="check-circle-rounded.svg"
      title={t('pages.ad.ad_is_restored')}
      description={description}
      actions={
        <Button
          data-testid={CommonTestIds.warningModal.actionButton}
          appearance="secondary"
          isFullWidth
          borderRadius={14}
          disabled={isLoading}
          onClick={onOkClick}
        >
          {t('common.got_it')}
        </Button>
      }
    />
  );
};

export const RestoredAdPopupWithRef = React.forwardRef(RestoredAdPopup);
